import { Component, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from '../services/AuthService';
import { RegistryService } from '../services/RegistryService';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { NetworkService } from '../services/NetworkService';
import { Toast } from 'primeng/toast';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Dialog, Button, Toast, TranslocoPipe],
  template: `
    @if(applicationInitialized()) {
      <p-toast [position]="this.auth.rtl() ? 'top-left': 'top-right'" />
      <router-outlet />
      <p-dialog
        (close)="net.showNetworkError.set(false)"
        header="Network Error"
        [modal]="true"
        [(visible)]="net.showNetworkError"
        [style]="{ width: '25rem' }">
        <h1 class="h-1">{{'Something wrong with network'|transloco}}</h1>
        <div class="flex justify-end gap-2">
          <p-button label="Ok" severity="secondary" (click)="net.showNetworkError.set(false)" />
        </div>
      </p-dialog>
    }
  `
})
export class App {
  public applicationInitialized = signal(false)
  public t = inject(TranslocoService)
  public net = inject(NetworkService)
  public registry = inject(RegistryService)
  public auth = inject(AuthService)
  public router = inject(Router)

  constructor() {
    console.log("App constructor")
    this.init().catch();
    document.documentElement.setAttribute('dir', localStorage.getItem('dir') || 'rtl');
    document.documentElement.setAttribute('lang', localStorage.getItem('lang') || 'he_il');
  }

  async init() {
    console.log("App init")

    const script = document.createElement('script');
    script.src = environment.services.schedule + '/widgets/assignments/index.js';
    script.async = true;
    script.crossOrigin = "anonymous";
    script.type = "module";
    document.body.appendChild(script);

    const tasksScript = document.createElement('script');
    tasksScript.src = environment.services.tasks + '/task-settings-widget.js';
    tasksScript.async = true;
    tasksScript.crossOrigin = "anonymous";
    tasksScript.type = "module";
    document.body.appendChild(tasksScript);

    // 0. Wait for transloco to load the active language
    console.log("Waiting for transloco to load")
    try {
      await firstValueFrom(this.t.events$.pipe(
        filter(e => e.type === "translationLoadSuccess" && e.payload.langName === this.t.getActiveLang())
      ));
      console.log("Transloco loaded successfully")
    } catch (e) {
      console.warn('Transloco load timed out or failed, continuing...', e);
    }

    // 1. Auto-login or redirect
    const urlParams = new URLSearchParams(window.location.search);
    const hash = urlParams.get('hash');

    if (hash) { // && !this.auth.isLoggedIn()
      try {
        if (/^\d+$/.test(hash) && hash.length > 6) {
          await this.auth.loginByPhone(hash, false);
          console.log("Auto-login via phone hash successful");
        } else if (/^[a-zA-Z0-9]{6}$/.test(hash)) {
          await this.auth.loginByHash(hash, false);
          console.log("Auto-login via login hash successful");
        } else {
          console.warn("Auto-login failed: unsupported hash format");
          await this.router.navigateByUrl('/login');
        }
      } catch (e: any) {
        console.error("Auto-login via hash failed", e);
        if (e?.message !== 'Access denied: Unauthorized role') {
          alert(this.t.translate("Login failed"));
        }
        await this.router.navigateByUrl('/login');
      }
    } else if (!this.auth.isLoggedIn()) {
      if (window.location.pathname !== '/login') {
        await this.router.navigateByUrl('/login');
      }
    }

    // 2. If logged in, load user and services (token validation happens via loadUser)
    console.log("Loading initial data")
    if (this.auth.isLoggedIn()) {
      try {
        // Note: auth.loginByPhone already calls loadUser and loadServices if successful.
        // But for cases where we already have a token in localStorage, we still need this.
        if (!this.auth.currentUser()) {
          await Promise.all([
            this.auth.loadUser(),
            this.registry.loadServices()
          ]);
        }
        console.log("Initial data loaded successfully")
      } catch (error) {
        console.error('Initial data load failed:', error);
      }
    }

    this.applicationInitialized.set(true);
  }
}
